import { useState } from 'react';
import { Search, Package, Truck, MapPin, CheckCircle2, Clock, AlertTriangle, ExternalLink, Phone, Loader2 } from 'lucide-react';
import { cn } from '../lib/utils';

const API_URL = '';

const STATUS_CONFIG = {
  en_attente:        { label: 'En attente de prise en charge', icon: Clock, color: 'text-yellow-600', bg: 'bg-yellow-50 border-yellow-200' },
  en_preparation:    { label: 'Colis pris en charge par World Express', icon: Package, color: 'text-blue-600', bg: 'bg-blue-50 border-blue-200' },
  expediee:          { label: 'En cours de livraison', icon: Truck, color: 'text-indigo-600', bg: 'bg-indigo-50 border-indigo-200' },
  au_bureau:         { label: 'Disponible au bureau (pret a etre recupere)', icon: MapPin, color: 'text-purple-600', bg: 'bg-purple-50 border-purple-200' },
  livree:            { label: 'Livre', icon: CheckCircle2, color: 'text-green-600', bg: 'bg-green-50 border-green-200' },
  echec_livraison:   { label: 'Tentative echouee — recontacter la cliente', icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50 border-red-200' },
  retournee:         { label: 'Colis retourne', icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50 border-red-200' },
};

export default function TrackingPage() {
  const [query, setQuery] = useState('');
  const [searchType, setSearchType] = useState('order');
  const [result, setResult] = useState(null);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    setLoading(true);
    setResult(null);
    setResults(null);
    setSearched(true);

    try {
      if (searchType === 'phone') {
        const phone = query.replace(/\s|-/g, '');
        const res = await fetch(`${API_URL}/api/tracking-by-phone/${phone}`);
        const data = await res.json();
        setResults(data.orders || []);
      } else {
        const res = await fetch(`${API_URL}/api/tracking/${query.trim()}`);
        const data = await res.json();
        setResult(data);
      }
    } catch {
      setResult({ found: false, message: 'Erreur de connexion' });
    } finally {
      setLoading(false);
    }
  };

  const StatusDisplay = ({ status }) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.en_attente;
    const Icon = config.icon;
    return (
      <div className={cn('flex items-center gap-3 p-4 border rounded-lg', config.bg)} data-testid="order-status">
        <Icon size={24} className={config.color} />
        <span className={cn('font-medium', config.color)}>{config.label}</span>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#F5F4F2]">
      {/* Header */}
      <div className="bg-[#1A1514] text-white py-16 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <Truck size={40} className="mx-auto mb-4 text-[#D4AF37]" />
          <h1 className="font-serif text-3xl sm:text-4xl mb-3" data-testid="tracking-title">Suivi de commande</h1>
          <p className="text-gray-400 text-sm">Entrez votre numero de commande ou telephone pour suivre votre colis</p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 -mt-8">
        {/* Search form */}
        <div className="bg-white shadow-lg p-6" data-testid="tracking-search">
          <div className="flex gap-2 mb-4">
            <button onClick={() => setSearchType('order')} className={cn('flex-1 py-2 text-sm font-medium border-b-2 transition-colors', searchType === 'order' ? 'border-[#1A1514] text-[#1A1514]' : 'border-transparent text-[#6B6A68]')} data-testid="search-by-order">
              <Package size={14} className="inline mr-1" /> Par commande
            </button>
            <button onClick={() => setSearchType('phone')} className={cn('flex-1 py-2 text-sm font-medium border-b-2 transition-colors', searchType === 'phone' ? 'border-[#1A1514] text-[#1A1514]' : 'border-transparent text-[#6B6A68]')} data-testid="search-by-phone">
              <Phone size={14} className="inline mr-1" /> Par telephone
            </button>
          </div>

          <form onSubmit={handleSearch} className="flex gap-2">
            <input
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder={searchType === 'order' ? 'Ex: BL-2026-XXXXXX' : 'Ex: 0555123456'}
              className="flex-1 border border-[#E0DFDD] p-3 text-sm focus:outline-none focus:border-[#1A1514]"
              data-testid="tracking-input"
            />
            <button type="submit" disabled={loading} className="bg-[#1A1514] text-white px-6 py-3 text-sm font-medium flex items-center gap-2 disabled:opacity-50" data-testid="tracking-search-btn">
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
              Rechercher
            </button>
          </form>
        </div>

        {/* Single order result */}
        {result && (
          <div className="mt-6 bg-white shadow p-6" data-testid="tracking-result">
            {result.found ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-[#6B6A68]">Commande</p>
                    <p className="font-mono font-bold text-lg">#{result.order_id}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-[#6B6A68]">Client</p>
                    <p className="text-sm font-medium">{result.customer_name}</p>
                  </div>
                </div>

                <StatusDisplay status={result.status} />

                <div className="text-sm space-y-2">
                  <div className="flex justify-between"><span className="text-[#6B6A68]">Wilaya</span><span>{result.wilaya}</span></div>
                  {result.ecotrack_tracking && (
                    <div className="flex justify-between items-center">
                      <span className="text-[#6B6A68]">Tracking World Express</span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs">{result.ecotrack_tracking}</span>
                        <a href={result.ecotrack_url} target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] hover:underline" data-testid="ecotrack-link">
                          <ExternalLink size={14} />
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-[#6B6A68]">
                <Package size={40} className="mx-auto mb-4 opacity-30" />
                <p>{result.message || 'Commande introuvable'}</p>
                <p className="text-xs mt-2">Verifiez le numero et reessayez</p>
              </div>
            )}
          </div>
        )}

        {/* Phone search results */}
        {results !== null && (
          <div className="mt-6 space-y-3" data-testid="tracking-results-list">
            {results.length === 0 ? (
              <div className="bg-white shadow p-8 text-center text-[#6B6A68]">
                <Phone size={40} className="mx-auto mb-4 opacity-30" />
                <p>Aucune commande trouvee pour ce numero</p>
              </div>
            ) : (
              results.map(o => {
                const config = STATUS_CONFIG[o.order_status] || STATUS_CONFIG.en_attente;
                const Icon = config.icon;
                return (
                  <div key={o.order_id} className="bg-white shadow p-4 flex items-center justify-between" data-testid={`result-${o.order_id}`}>
                    <div>
                      <p className="font-mono font-bold text-sm">#{o.order_id}</p>
                      <p className="text-xs text-[#6B6A68] mt-0.5">{o.wilaya} — {o.total?.toLocaleString()} DA</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Icon size={16} className={config.color} />
                      <span className={cn('text-xs font-medium', config.color)}>{config.label}</span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {!searched && (
          <div className="mt-8 text-center text-[#6B6A68] text-sm">
            <p>Livraison assuree par <strong>World Express</strong></p>
            <p className="mt-1">Delai moyen : 3 a 7 jours ouvrables</p>
          </div>
        )}
      </div>
    </div>
  );
}
